import { z } from "zod";

export const FORMSPREE_ENDPOINT = "https://formspree.io/f/mdawyovp";

export const leadFormSchema = z.object({
  name: z.string().trim().min(1, "Please enter your Full Name.").max(100, "Full Name must be 100 characters or fewer."),
  email: z
    .string()
    .trim()
    .min(1, "Please enter your Email Address.")
    .max(255, "Email Address must be 255 characters or fewer.")
    .email("Please enter a valid Email Address (e.g. you@example.com)."),
  phone: z.string().trim().max(30, "Phone Number must be 30 characters or fewer.").regex(/^[+\d\s-]*$/, "Phone Number can only contain numbers, spaces, and dashes.").optional().or(z.literal("")),
  subject: z.string().trim().min(1, "Please select an option from the 'I'm Interested In' dropdown.").max(120, "Subject must be 120 characters or fewer."),
  message: z.string().trim().min(1, "Please enter Your Message.").max(1000, "Your Message must be 1000 characters or fewer."),
});

export const shortLeadFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(100, "Name must be 100 characters or fewer."),
  email: z.string().trim().max(255, "Email must be 255 characters or fewer.").optional().or(z.literal("")),
  phone: z.string().trim().max(20, "Phone Number must be 20 characters or fewer.").optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required.").max(1000, "Message must be 1000 characters or fewer."),
});

export type LeadFormInput = z.input<typeof leadFormSchema>;
export type LeadFormValues = z.infer<typeof leadFormSchema>;
export type ShortLeadFormInput = z.input<typeof shortLeadFormSchema>;
export type ShortLeadFormValues = z.infer<typeof shortLeadFormSchema>;
export type LeadFormSource = "contact-page" | "home-contact-cta" | "floating-enquire" | "discovery-call-popup";

export const emptyLeadForm: LeadFormInput = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export const emptyShortLeadForm: ShortLeadFormInput = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

const sourceLabelMap: Record<LeadFormSource, string> = {
  "contact-page": "Contact Page",
  "home-contact-cta": "Home Contact CTA",
  "floating-enquire": "Floating Enquire Button",
  "discovery-call-popup": "Discovery Call Popup",
};

export const validateLeadForm = (values: LeadFormInput): LeadFormValues => {
  const parsed = leadFormSchema.parse(values);

  return {
    ...parsed,
    phone: parsed.phone?.trim() ?? "",
    subject: parsed.subject?.trim() || "General Inquiry",
  };
};

export const validateShortLeadForm = (values: ShortLeadFormInput): ShortLeadFormValues => {
  const parsed = shortLeadFormSchema.parse(values);

  return {
    ...parsed,
    email: parsed.email?.trim() ?? "",
    phone: parsed.phone?.trim() ?? "",
  };
};

export const getLeadFormErrorMessage = (error: unknown) => {
  if (error instanceof z.ZodError) {
    return error.issues[0]?.message ?? "Please check your details and try again.";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

const sanitizePageUrl = (): string => {
  try {
    const u = new URL(window.location.href);
    ["__lovable_token", "lovable_token", "token"].forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return "";
  }
};

const submitToFormspree = async (payload: Record<string, string>) => {
  let response: Response;
  try {
    response = await fetch(FORMSPREE_ENDPOINT, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error(
      "Your message couldn't be sent — this is usually caused by an ad blocker or privacy extension. Please disable it and try again, or use a different browser."
    );
  }

  const responseData = await response.json().catch(() => null);

  if (!response.ok || (responseData && typeof responseData === "object" && "errors" in responseData)) {
    const formspreeMessage =
      responseData &&
      typeof responseData === "object" &&
      "errors" in responseData &&
      Array.isArray(responseData.errors)
        ? responseData.errors.find(
            (item): item is { message?: string } => Boolean(item) && typeof item === "object",
          )?.message
        : null;

    throw new Error(formspreeMessage || "We couldn't send your message right now. Please try again.");
  }
};

export const submitLeadForm = async (values: LeadFormInput, source: LeadFormSource) => {
  const validatedValues = validateLeadForm(values);

  await submitToFormspree({
    ...validatedValues,
    formSource: source,
    pageUrl: typeof window !== "undefined" ? sanitizePageUrl() : "",
    _subject: `MVIBE Lead - ${sourceLabelMap[source]}`,
  });

  return validatedValues;
};

export const submitShortLeadForm = async (values: ShortLeadFormInput, source: Extract<LeadFormSource, "floating-enquire">) => {
  const validatedValues = validateShortLeadForm(values);

  await submitToFormspree({
    name: validatedValues.name,
    email: validatedValues.email,
    phone: validatedValues.phone,
    subject: "Service Enquiry",
    message: validatedValues.message,
    formSource: source,
    pageUrl: typeof window !== "undefined" ? sanitizePageUrl() : "",
    _subject: `MVIBE Lead - ${sourceLabelMap[source]}`,
  });

  return validatedValues;
};
